import { CallExpressionShape, IdentifierShape, PropertyAccessShape } from '@servicenow/sdk-build-core'
import { NowIdShape } from '@servicenow/sdk-build-plugins'
import { ACTIVITY_DEFAULTS, ACTIVITY_TABLE, LANE_DEFAULTS, LANE_TABLE } from '../constants/playbook-plugin-constants'
import type { Database, Record as SdkRecord, Shape } from '@servicenow/sdk-build-core'

// Parsing (Fluent → database) accepts both the top-level Run.* form and the
// namespaced wfa.playbook.run.* form so existing fluent keeps working.
export const RUN_CALLEE_TO_START_RULE: Record<string, string> = {
    'wfa.playbook.run.Immediately': 'immediate',
    'wfa.playbook.run.After': 'after_items',
    'wfa.playbook.run.Manually': 'manual',
}

// Generation (database → Fluent) emits the namespaced wfa.playbook.run.* form.
export const START_RULE_TO_RUN_CALLEE: Record<string, string> = {
    immediate: 'wfa.playbook.run.Immediately',
    after_items: 'wfa.playbook.run.After',
    manual: 'wfa.playbook.run.Manually',
}

/** Both the top-level and namespaced callees for Run.After(). */
export const RUN_AFTER_CALLEES = new Set(['wfa.playbook.run.After'])

/** True when a callee string is either Run.After or wfa.playbook.run.After. */
export function isRunAfterCallee(callee: string): boolean {
    return RUN_AFTER_CALLEES.has(callee)
}

/** toRecord direction: CallExpressionShape → database string */
export function resolveStartRuleToDatabase(startRule: unknown): unknown {
    if (startRule instanceof CallExpressionShape) {
        return RUN_CALLEE_TO_START_RULE[startRule.getCallee()] ?? startRule
    }
    return startRule
}

const TABLE_CONFIG: Record<string, { startsAfterField: string; defaultStartRule: string }> = {
    [LANE_TABLE]: { startsAfterField: 'starts_after_lanes', defaultStartRule: LANE_DEFAULTS.START_RULE },
    [ACTIVITY_TABLE]: { startsAfterField: 'starts_after_activities', defaultStartRule: ACTIVITY_DEFAULTS.START_RULE },
}

/** toShape direction: database record → Run.*() CallExpressionShape */
export function resolveStartRuleToShape(
    record: SdkRecord,
    database: Database,
    guidToVarName?: Map<string, string>,
    branchGuidMap?: Map<string, { activityVarName: string; fluentBranchId: string }>
): { startRuleShape: CallExpressionShape | ReturnType<SdkRecord['get']> } {
    const config = TABLE_CONFIG[record.getTable()]
    if (!config) {
        return { startRuleShape: record.get('start_rule') }
    }

    const startRuleRaw = record.get('start_rule')?.ifString()?.getValue() ?? ''
    const startRuleValue = START_RULE_TO_RUN_CALLEE[startRuleRaw]
        ? startRuleRaw
        : (record.get('start_rule_name')?.ifString()?.getValue() ?? config.defaultStartRule)
    const runCallee = START_RULE_TO_RUN_CALLEE[startRuleValue]
    const isRunAfter = isRunAfterCallee(runCallee)
    const startRuleShape = isRunAfter
        ? resolveRunAfterShape(record, database, guidToVarName, branchGuidMap)
        : runCallee
          ? new CallExpressionShape({ source: record, callee: runCallee, args: [] })
          : record.get('start_rule')

    return { startRuleShape }
}

function resolveRunAfterShape(
    record: SdkRecord,
    database: Database,
    guidToVarName?: Map<string, string>,
    branchGuidMap?: Map<string, { activityVarName: string; fluentBranchId: string }>
): CallExpressionShape {
    const args: Shape[] = []
    const table = record.getTable()
    const startsAfterBranchesValue = record.get('starts_after_branches')?.ifString()?.getValue() ?? ''
    const branchEntries = startsAfterBranchesValue
        .split(',')
        .map((g) => g.trim())
        .filter(Boolean)
    const branchActivityGuids = new Set(
        branchEntries
            .map((entry) => {
                const dotIndex = entry.indexOf('.')
                return dotIndex >= 0 ? entry.substring(0, dotIndex) : ''
            })
            .filter(Boolean)
    )

    // Resolve starts_after_lanes → IdentifierShape or NowIdShape
    const startsAfterLanesValue = record.get('starts_after_lanes')?.ifString()?.getValue() ?? ''
    const laneGuids = startsAfterLanesValue
        .split(',')
        .map((g) => g.trim())
        .filter(Boolean)
    for (const guid of laneGuids) {
        const varName = guidToVarName?.get(guid)
        if (varName) {
            args.push(new IdentifierShape({ source: record, name: varName }))
        } else {
            const depRecord = database.get(table, guid)
            if (depRecord) {
                args.push(NowIdShape.from(depRecord))
            }
        }
    }

    // Resolve starts_after_activities → IdentifierShape or NowIdShape
    const startsAfterActivitiesValue = record.get('starts_after_activities')?.ifString()?.getValue() ?? ''
    const activityGuids = startsAfterActivitiesValue
        .split(',')
        .map((g) => g.trim())
        .filter(Boolean)
    for (const guid of activityGuids) {
        if (branchActivityGuids.has(guid)) {
            continue
        }
        const varName = guidToVarName?.get(guid)
        if (varName) {
            args.push(new IdentifierShape({ source: record, name: varName }))
        } else {
            const activityRecord = database.get(ACTIVITY_TABLE, guid)
            if (activityRecord) {
                args.push(NowIdShape.from(activityRecord))
            }
        }
    }

    // Resolve starts_after_branches → PropertyAccessShape (e.g. activity_0.branches.critical)
    // Platform format: <decisionActivitySysId>.<branchId> (e.g. "abc123.def456")
    for (const entry of branchEntries) {
        const dotIndex = entry.indexOf('.')
        const branchGuid = dotIndex >= 0 ? entry.substring(dotIndex + 1) : entry
        const branchInfo = branchGuidMap?.get(branchGuid)
        if (branchInfo) {
            args.push(
                new PropertyAccessShape({
                    source: record,
                    elements: [branchInfo.activityVarName, 'branches', branchInfo.fluentBranchId] as [
                        string,
                        string,
                        ...string[],
                    ],
                })
            )
        }
    }

    return new CallExpressionShape({ source: record, callee: START_RULE_TO_RUN_CALLEE.after_items, args })
}
